import { useCallback, useEffect, useState, type ChangeEvent } from 'react';

import { snapPremiumToStep } from '@/features/guided-financial-planning/utils/premium-bounds-from-quotes';

export interface UsePremiumAmountInputParams {
  clampedValue: number;
  min: number;
  max: number;
  step: number;
  onPersist: (value: number) => void;
  /**
   * When true, clears any in-progress typed draft. Pass a dialog `open` flag
   * so each open starts from the current value, not a stale draft.
   */
  resetDraftWhenOpen?: boolean;
}

function formatIntegerForInput(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

export function usePremiumAmountInput({
  clampedValue,
  min,
  max,
  step,
  onPersist,
  resetDraftWhenOpen,
}: UsePremiumAmountInputParams) {
  /** Digits-only draft while editing; `null` means show committed formatted value. */
  const [amountDraft, setAmountDraft] = useState<string | null>(null);

  useEffect(() => {
    if (resetDraftWhenOpen) {
      setAmountDraft(null);
    }
  }, [resetDraftWhenOpen]);

  const getResolvedValue = useCallback((): number => {
    if (amountDraft !== null) {
      const digits = amountDraft.replace(/[^0-9]/g, '');
      if (digits === '') {
        return snapPremiumToStep(clampedValue, min, max, step);
      }
      const parsed = Number(digits);
      if (!Number.isFinite(parsed)) {
        return snapPremiumToStep(clampedValue, min, max, step);
      }
      const clamped = Math.max(min, Math.min(max, parsed));
      return snapPremiumToStep(clamped, min, max, step);
    }
    return snapPremiumToStep(clampedValue, min, max, step);
  }, [amountDraft, clampedValue, min, max, step]);

  const commitDraft = useCallback((): number => {
    const next = getResolvedValue();
    onPersist(next);
    setAmountDraft(null);
    return next;
  }, [getResolvedValue, onPersist]);

  const handlePremiumInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/[^0-9]/g, '');
      if (raw === '') {
        setAmountDraft('');
        return;
      }
      const parsed = Number(raw);
      if (!Number.isFinite(parsed)) {
        return;
      }
      if (parsed > max) {
        setAmountDraft(String(Math.round(max)));
        return;
      }
      setAmountDraft(raw);
    },
    [max],
  );

  const handlePremiumInputFocus = useCallback(() => {
    setAmountDraft(String(Math.round(clampedValue)));
  }, [clampedValue]);

  const inputDisplayValue =
    amountDraft !== null
      ? amountDraft === ''
        ? ''
        : formatIntegerForInput(Number(amountDraft))
      : formatIntegerForInput(Math.round(clampedValue));

  const clearAmountDraft = useCallback(() => {
    setAmountDraft(null);
  }, []);

  return {
    getResolvedValue,
    commitDraft,
    handlePremiumInputChange,
    handlePremiumInputFocus,
    inputDisplayValue,
    clearAmountDraft,
  };
}
